import GsuLoader from '@/components/GsuLoader';
import { PageMetaInfo } from '@/compositions/MetaInfo';
import { FragClassPageMetaInfo } from '@/compositions/MetaInfo/query';
import { HandleOnLoadAction } from '@/compositions/OnLoadAction';
import { FragOnLoadAction } from '@/compositions/OnLoadAction/query';
import type { Props as PageComposerProps } from '@/compositions/PageComposer';
import { FragComposerSections } from '@/compositions/PageComposer/query';
import PageLayout from '@/compositions/PageLayout';
import { coreLayoutContainerAside, coreLayoutSection } from '@/constants/measured-scope';
import { queryGsuApi } from '@/service/query';
import { Page } from '@/shared/api-generated-types';
import type { GetServerSideProps, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import { gql, useQuery } from 'urql';

// PageComposer dynamic import to prevent SSR hydration issues
const PageComposerWithLoader = dynamic<PageComposerProps>(() => import('@/compositions/PageComposer'), {
  ssr: false,
  loading: () => <GsuLoader consumeSpace />
});
const PageComposer = dynamic<PageComposerProps>(() => import('@/compositions/PageComposer'), {
  ssr: false
});
const QUERY_CLASS_DETAIL = gql`
  ${FragComposerSections}
  ${FragClassPageMetaInfo}
  ${FragOnLoadAction}

  query ClassDetail($classId: ID!) {
    page: classDetailPage(classId: $classId) {
      title
      id
      metaInfo {
        ...ClassPageMetaInfo
      }
      onLoadAction {
        ...OnLoadAction
      }
      sections {
        ...CoreSections
        ...RecursiveSections
      }
    }
  }
`;
const heroContent = "hpax59d";
const QUERY_PAGE_ONLOAD_ACTION_TO_COOKIE_CLEANUP = gql`
  ${FragOnLoadAction}

  query OnLoadActionCleanUpQuery($classId: ID!) {
    page: classDetailPage(classId: $classId) {
      id
      catalogueLanguage
      title
      onLoadAction {
        ...OnLoadAction
      }
    }
  }
`;
const Classes: NextPage<{
  classId: string;
}> = props => {
  const [{
    data,
    fetching
  }] = useQuery({
    query: QUERY_CLASS_DETAIL,
    variables: {
      classId: props.classId
    },
    requestPolicy: 'cache-first',
    pause: !props.classId
  });

  /**
   * ⚠️ It is important for this client-side request to be triggered.
   * This will allow GQL to clear necessary cookies.
   * The onLoadAction must still be passed into the props for the page to
   * actually *do* any onLoadAction. Such as displaying a dialog.
   */
  useQuery({
    query: QUERY_PAGE_ONLOAD_ACTION_TO_COOKIE_CLEANUP,
    variables: {
      classId: props.classId
    }
  });
  const pageData: Page = data?.page || {};
  const hasSimilarClasses = useMemo(() => {
    try {
      return pageData?.sections?.find(section => section.__typename === 'SimilarClassesPageSection');
    } catch (error) {
      return false;
    }
  }, [pageData]);
  const hasNextSession = useMemo(() => {
    try {
      return pageData?.sections?.find(section => section.__typename === 'NextSessionSection');
    } catch (error) {
      return false;
    }
  }, [pageData]);
  const hasUpcomingSessions = useMemo(() => {
    try {
      return pageData?.sections?.find(section => section.__typename === 'UpcomingPageSection');
    } catch (error) {
      return false;
    }
  }, [pageData]);
  const hasSponsoredSection = useMemo(() => {
    try {
      return pageData?.sections?.find(section => section.__typename === 'SponsorInfoPageSection');
    } catch (error) {
      return false;
    }
  }, [pageData]);
  return <PageLayout>
      {pageData.metaInfo ? <PageMetaInfo {...pageData.metaInfo} /> : null}
      {pageData.onLoadAction ? <HandleOnLoadAction {...pageData.onLoadAction} /> : null}
      <section className={coreLayoutSection}>
        <div className={`${coreLayoutContainerAside}`}>
          <div>
            <div className={heroContent}>
              <PageComposerWithLoader fetching={fetching} {...pageData} includeSections={['ClassSummaryPageSection', 'ImagePageSection']} />
            </div>
            {hasSponsoredSection ? <PageComposerWithLoader fetching={fetching} {...pageData} includeSections={['SponsorInfoPageSection']} sectionClassNames={{
            SponsorInfoPageSection: 'mt-0 mb-md'
          }} /> : null}
            <PageComposer fetching={fetching} {...pageData} excludeSections={['UpcomingPageSection', 'NextSessionSection', 'SimilarClassesPageSection', 'ClassSummaryPageSection', 'ImagePageSection', 'SponsorInfoPageSection']} />
          </div>
          {hasUpcomingSessions ? <aside className="pt-xl p-0-gte-lg">
              <PageComposer fetching={fetching} {...pageData} includeSections={['UpcomingPageSection']} />
            </aside> : null}
        </div>
      </section>

      {hasSimilarClasses ? <PageComposer fetching={fetching} {...pageData} includeSections={['SimilarClassesPageSection']} /> : null}
      {hasNextSession ? <PageComposer fetching={fetching} {...pageData} includeSections={['NextSessionSection']} /> : null}
    </PageLayout>;
};
export const getServerSideProps: GetServerSideProps = async ({
  locale = 'en-US',
  req,
  res,
  query: {
    slug,
    id
  }
}) => {
  const [urqlState, statusCode] = await queryGsuApi(QUERY_CLASS_DETAIL, {
    classId: id
  }, req);
  if (!!statusCode) {
    console.log('ClassDetails page failed to retrieve details', statusCode);
    res.statusCode = statusCode;
    return {
      notFound: true
    };
  }
  return {
    props: {
      ...(await serverSideTranslations(locale, ['badge', 'common', 'classes'])),
      classId: id,
      classSlug: slug,
      urqlState
    }
  };
};
export default Classes;

require("./index.linaria.module.css!=!../../../../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js?cacheProvider=!./index.tsx");